import { type ChangeEvent, memo, useCallback, useEffect, useState } from 'react';

import { Input } from '@air/primitive-input';

import { useShowcaseModal } from '~/components/ShowcaseModal/ShowcaseModalContext';

export const ShowcaseNameSetting = memo(() => {
  const { showcaseProps, updateShowcaseName, saveShowcaseName, isEditMode, errors, isSaving } = useShowcaseModal();
  const [name, setName] = useState<string>(showcaseProps.name);

  // Sync local state with context changes (e.g., after save)
  useEffect(() => {
    setName(showcaseProps.name);
  }, [showcaseProps.name]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newName = e.target.value;
      setName(newName);
      // Always update parent state immediately for validation feedback
      updateShowcaseName(newName);
    },
    [updateShowcaseName],
  );

  const handleBlur = useCallback(() => {
    const hasChanges = name !== showcaseProps.name;
    const hasErrors = !name || !!errors?.name;

    // Only save if in edit mode, there are changes, and validation passes
    if (isEditMode && hasChanges && !hasErrors) {
      saveShowcaseName?.();
    }
  }, [name, showcaseProps.name, errors?.name, isEditMode, saveShowcaseName]);

  return (
    <div>
      <div className="relative flex items-center">
        <Input
          size="large"
          value={name}
          data-testid="COLLECTION_NAME_SETTING_INPUT"
          onChange={handleChange}
          name="collectionName"
          className="w-full rounded-8"
          placeholder="Enter showcase name"
          onBlur={handleBlur}
          disabled={isSaving}
        />
      </div>
      {errors?.name && <p className="ads:text-error text-12 text-red-9">{errors.name}</p>}
    </div>
  );
});

ShowcaseNameSetting.displayName = 'ShowcaseNameSetting';
